import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { subDays } from "date-fns";

import { supabase } from "@/integrations/supabase/client";
import { capture } from "@/lib/sentry/capture";
import { countOpenIssuesByProject } from "@/stores/pm/repository";
import { logger } from "@/utils/logger";

export interface ProjectActivity {
  /** Documents uploaded to the project in the last 7 days. */
  uploads7d: number;
  /** Open (non-closed) PM issues in the project. 0 when issues are excluded. */
  openIssues: number;
  /** Newest upload timestamp within the 7-day window, or null. */
  lastUploadAt: string | null;
}

function assertCompleteUploads(docs: readonly unknown[] | null, count: number | null): void {
  if (count == null || (docs?.length ?? 0) !== count)
    throw new Error("Project activity document results are incomplete");
}

function collectUploads(
  docs: Array<{ project_id: number | null; created_at: string }> | null,
  entryFor: (projectId: number) => ProjectActivity
): void {
  for (const doc of docs ?? []) {
    if (doc.project_id == null) continue;
    const entry = entryFor(doc.project_id);
    entry.uploads7d += 1;
    if (!entry.lastUploadAt || doc.created_at > entry.lastUploadAt) {
      entry.lastUploadAt = doc.created_at;
    }
  }
}

/**
 * Per-project activity aggregates for one building — the "what is happening
 * in this project" line on the dashboard's project cards. The bounded document
 * read verifies its exact count before grouping uploads and open-issue counts
 * into a map keyed by project id.
 *
 * Pass `includeIssues: false` when the PM module is disabled or the user
 * lacks the PM tool permission — the issues query is skipped entirely.
 */
export function useProjectsActivity(
  buildingId: string | undefined,
  { enabled = true, includeIssues = true }: { enabled?: boolean; includeIssues?: boolean } = {}
) {
  const query = useQuery<Map<number, ProjectActivity>, unknown>({
    queryKey: ["building-projects-activity", buildingId, includeIssues],
    enabled: !!buildingId && enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const since = subDays(new Date(), 7).toISOString();
      const activity = new Map<number, ProjectActivity>();
      const entryFor = (projectId: number): ProjectActivity => {
        let entry = activity.get(projectId);
        if (!entry) {
          entry = { uploads7d: 0, openIssues: 0, lastUploadAt: null };
          activity.set(projectId, entry);
        }
        return entry;
      };

      const {
        data: docs,
        error: docsError,
        count,
      } = await supabase
        .from("documents")
        .select("project_id, created_at", { count: "exact" })
        .eq("building_id", Number(buildingId))
        .eq("hidden", false)
        .is("deleted_at", null)
        .not("project_id", "is", null)
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .order("id", { ascending: false })
        .range(0, 999);
      if (docsError) throw docsError;
      assertCompleteUploads(docs, count);

      collectUploads(docs, entryFor);

      if (includeIssues) {
        const issueCounts = await countOpenIssuesByProject(Number(buildingId));
        for (const [projectId, count] of issueCounts) {
          entryFor(projectId).openIssues = count;
        }
      }

      return activity;
    },
  });

  const { isError, error, errorUpdatedAt } = query;
  useEffect(() => {
    if (!isError) return;

    const reportableError =
      error instanceof Error
        ? error
        : new Error(
            typeof error === "string"
              ? error
              : typeof error === "object" &&
                  error !== null &&
                  "message" in error &&
                  typeof error.message === "string"
                ? error.message
                : "Project activity query failed"
          );
    const sentryEventId = capture(reportableError, {
      tags: { area: "projects", operation: "project_activity_load" },
      fingerprint: ["projects", "activity", "load"],
      context: { project_activity: { buildingId, includeIssues } },
    });
    void logger.error(
      "database",
      "project_activity_load",
      "Failed to load project activity",
      reportableError,
      {
        building_id: buildingId,
        include_issues: includeIssues,
        sentry_event_id: sentryEventId,
      }
    );
  }, [isError, error, errorUpdatedAt, buildingId, includeIssues]);

  return query;
}
